import { isCalculationTermComplete } from '@bindoc-projects/util-domain-lib';
import type { OverrideProperty } from '@bindoc-projects/util-domain-lib';
import type { ShareableType } from '@bindoc-projects/resource-domain-lib';
import { VirtualClinicAnalysisOperatingNumberTerm } from './term';
import { VirtualClinicAnalysisFullyInpatientCasesFilter } from '../filter/fully-inpatient-cases';
import type {
  FilterRegistrationToken} from '@bindoc-projects/filter-condition-domain-lib';
import {
  FilterConditionUtil
} from '@bindoc-projects/filter-condition-domain-lib';
import type { FilterConditionDto } from '@bindoc-projects/condition-domain-lib';
import type { VirtualClinicAnalysisOperatingNumberFullyInpatientCaseTermSpec } from '../../resolving';

export class VirtualClinicAnalysisFullyInpatientCaseOperatingNumber extends VirtualClinicAnalysisOperatingNumberTerm {
  public static async fromObject(
    obj: VirtualClinicAnalysisOperatingNumberFullyInpatientCaseTermSpec,
    registeredFilter: FilterRegistrationToken[],
  ): Promise<VirtualClinicAnalysisFullyInpatientCaseOperatingNumber> {
    let fullyInpatientFilter: VirtualClinicAnalysisFullyInpatientCasesFilter;

    if (!obj.caseFilter || Object.keys(obj.caseFilter).length === 0) {
      // Empty filter
      fullyInpatientFilter = new VirtualClinicAnalysisFullyInpatientCasesFilter();
    } else {
      // New condition format: {and: {conditions: [...]}} or {conditionId: {...}}
      const expression = await FilterConditionUtil.fromConditionJson(
        obj.caseFilter as FilterConditionDto,
        registeredFilter,
      );
      fullyInpatientFilter = new VirtualClinicAnalysisFullyInpatientCasesFilter(expression);
    }

    return new VirtualClinicAnalysisFullyInpatientCaseOperatingNumber(
      obj.operatingNumberId,
      fullyInpatientFilter,
    );
  }

  constructor(
    public operatingNumberId: string,
    public fullyInpatientCasesFilter: VirtualClinicAnalysisFullyInpatientCasesFilter,
  ) {
    super('fullyInpatientCaseOperatingNumber');
  }

  clone(): VirtualClinicAnalysisOperatingNumberTerm {
    return new VirtualClinicAnalysisFullyInpatientCaseOperatingNumber(
      this.operatingNumberId,
      this.fullyInpatientCasesFilter.clone(),
    );
  }

  overrideClone(
    override: OverrideProperty[],
  ): VirtualClinicAnalysisOperatingNumberTerm {
    return new VirtualClinicAnalysisFullyInpatientCaseOperatingNumber(
      this.operatingNumberId,
      this.fullyInpatientCasesFilter.overrideClone(override),
    );
  }

  getChildResources(): { [resourceName in ShareableType]?: string[] } {
    return this.fullyInpatientCasesFilter.getChildResources();
  }

  getFullyInpatientCasesFilters(): VirtualClinicAnalysisFullyInpatientCasesFilter[] {
    return [this.fullyInpatientCasesFilter];
  }

  getOverrideProperties(
    tileId: string,
    dependencyVirtualClinicId: string,
  ): OverrideProperty[] {
    return [
      ...this.fullyInpatientCasesFilter.getOverrideProperties(
        tileId,
        dependencyVirtualClinicId,
      ),
    ];
  }

  isValid(): boolean {
    // The shared rule covers the id; the case filter is this leaf's own extra
    // condition and has no counterpart in the Klinik-Cube, whose metrics are
    // pre-aggregated per clinic and have no sub-grain left to filter.
    return (
      isCalculationTermComplete<VirtualClinicAnalysisOperatingNumberTerm>(this) &&
      !!this.fullyInpatientCasesFilter &&
      this.fullyInpatientCasesFilter.expression.valid()
    );
  }

  toJSON(): VirtualClinicAnalysisOperatingNumberFullyInpatientCaseTermSpec {
    const condition = this.fullyInpatientCasesFilter.expression.toCondition({});
    return {
      type: 'fullyInpatientCaseOperatingNumber',
      operatingNumberId: this.operatingNumberId,
      caseFilter: condition?.toJSON() ?? {},
    };
  }

  onUpdateVirtualClinicId(virtualClinicId: string): void {
    this.fullyInpatientCasesFilter.onUpdateVirtualClinicId(virtualClinicId);
  }
}
